import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Users,
  CreditCard,
  MessageSquare,
  Smartphone,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Clock,
  DollarSign
} from 'lucide-react'
import { adminApi } from '@/lib/api'

interface DashboardMetrics {
  users: {
    today: number
    yesterday: number
    thisWeek: number
    thisMonth: number
    unverified: number
    banned: number
    active: number
    growth: {
      daily: number
      weekly: number
    }
  }
  sms: {
    today: number
    yesterday: number
    thisWeek: number
    thisMonth: number
    failed: number
    successRate: string
  }
  subscriptions: {
    active: number
    expired: number
    cancelled: number
    byPlan: Array<{ _id: string; count: number }>
    conversionRate: string
  }
  devices: {
    total: number
    active: number
    offline: number
    uptime: string
  }
  support: {
    open: number
    closed: number
    thisWeek: number
    resolution: string
  }
  revenue: {
    total: number
    average: number
    monthly: number
  }
}

export default function AdminDashboard() {
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['admin-dashboard-metrics'],
    queryFn: () => adminApi.get('/admin/analytics/dashboard-metrics').then((res) => res.data.data as DashboardMetrics),
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  const { data: systemStats } = useQuery({
    queryKey: ['admin-system-stats'],
    queryFn: () => adminApi.get('/admin/system-stats').then((res) => res.data.data),
    refetchInterval: 30000,
  })

  const MetricCard = ({ 
    title, 
    value, 
    change, 
    changeLabel, 
    icon: Icon, 
    color = 'blue',
    badge,
    badgeVariant = 'secondary' 
  }: {
    title: string
    value: string | number
    change?: number
    changeLabel?: string
    icon: any
    color?: string
    badge?: string
    badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline'
  }) => (
    <Card className="relative overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-600">
          {title}
        </CardTitle>
        <div className={`rounded-full p-2 bg-${color}-50`}>
          <Icon className={`h-4 w-4 text-${color}-600`} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="text-2xl font-bold text-gray-900">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </div>
          {badge && (
            <Badge variant={badgeVariant} className="text-xs">
              {badge}
            </Badge>
          )}
        </div>
        {change !== undefined && changeLabel && (
          <div className={`flex items-center mt-2 text-xs ${
            change >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {change >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
            {Math.abs(change)} {changeLabel}
          </div>
        )}
      </CardContent>
    </Card>
  )

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-gray-600 mt-2">
          System overview and real-time metrics for redgeGate
        </p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Total Users"
          value={metrics?.users.thisMonth || 0}
          change={metrics?.users.growth.weekly}
          changeLabel="this week"
          icon={Users}
          color="blue"
        />
        
        <MetricCard
          title="Active Subscriptions"
          value={metrics?.subscriptions.active || 0}
          icon={CreditCard}
          color="green"
          badge={`${metrics?.subscriptions.conversionRate || 0}% conversion`}
          badgeVariant="outline"
        />
        
        <MetricCard
          title="SMS This Month"
          value={metrics?.sms.thisMonth || 0}
          icon={MessageSquare}
          color="purple"
          badge={`${metrics?.sms.successRate || 0}% success`}
          badgeVariant="secondary"
        />
        
        <MetricCard
          title="Active Devices"
          value={`${metrics?.devices.active || 0}/${metrics?.devices.total || 0}`}
          icon={Smartphone}
          color="orange"
          badge={`${metrics?.devices.uptime || 0}% uptime`}
          badgeVariant="outline"
        />
      </div>

      {/* Secondary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Unverified Users"
          value={metrics?.users.unverified || 0}
          icon={AlertTriangle}
          color="yellow"
          badge="Needs attention"
          badgeVariant="destructive"
        />
        
        <MetricCard
          title="Support Tickets"
          value={`${metrics?.support.open || 0} open`}
          icon={Clock}
          color="red"
          badge={`${metrics?.support.resolution || 0}% resolved`}
          badgeVariant="outline"
        />
        
        <MetricCard
          title="Revenue (MTD)"
          value={`$${(metrics?.revenue.monthly || 0).toFixed(2)}`}
          icon={DollarSign}
          color="green"
        />
        
        <MetricCard
          title="System Health"
          value="Operational"
          icon={CheckCircle2}
          color="green"
          badge="All services"
          badgeVariant="default"
        />
      </div>

      {/* Recent Activity & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
              Growth Trends
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Users Today vs Yesterday</span>
              <div className="flex items-center">
                <span className="text-sm font-medium">
                  {metrics?.users.today || 0} vs {metrics?.users.yesterday || 0}
                </span>
                {(metrics?.users.growth.daily || 0) > 0 && (
                  <TrendingUp className="h-4 w-4 ml-2 text-green-600" />
                )}
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">SMS Today vs Yesterday</span>
              <div className="flex items-center">
                <span className="text-sm font-medium">
                  {metrics?.sms.today || 0} vs {metrics?.sms.yesterday || 0}
                </span>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Active Users (7 days)</span>
              <span className="text-sm font-medium">{metrics?.users.active || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2 text-amber-600" />
              System Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {metrics?.users.unverified && metrics.users.unverified > 0 && (
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2" />
                  <span className="text-sm">Unverified users</span>
                </div>
                <Badge variant="outline">{metrics.users.unverified}</Badge>
              </div>
            )}
            
            {metrics?.users.banned && metrics.users.banned > 0 && (
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center">
                  <AlertTriangle className="h-4 w-4 text-red-600 mr-2" />
                  <span className="text-sm">Banned users</span>
                </div>
                <Badge variant="destructive">{metrics.users.banned}</Badge>
              </div>
            )}
            
            {metrics?.support.open && metrics.support.open > 0 && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-blue-600 mr-2" />
                  <span className="text-sm">Open support tickets</span>
                </div>
                <Badge variant="outline">{metrics.support.open}</Badge>
              </div>
            )}

            {(!metrics?.users.unverified || metrics.users.unverified === 0) &&
             (!metrics?.users.banned || metrics.users.banned === 0) &&
             (!metrics?.support.open || metrics.support.open === 0) && (
              <div className="flex items-center p-3 bg-green-50 rounded-lg">
                <CheckCircle2 className="h-4 w-4 text-green-600 mr-2" />
                <span className="text-sm text-green-800">All systems operating normally</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
